<template>
  <div>
    <v-layout
      wrap
      align-center
    >
      <menu-pages-navbar class="mb-3" />
      <v-btn
        class="ml-auto mr-0 my-0"
        round
        color="info"
        :to="{name: 'menuitems', params: {menuId: menuId}}"
      >
        <v-icon left>
          close
        </v-icon>
        {{ $t('menuPreviewLeave') }}
      </v-btn>
    </v-layout>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        id="content-loading-indicator"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <v-layout
      justify-center
      align-center
      class="mt-4"
      wrap
    >
      <!-- Allowed MenuCategories -->
      <v-layout
        v-if="!loading"
        justify-end
        align-center
        wrap
      >
        <v-chip
          v-for="menuCategory in menuCategoriesWithItems"
          :key="menuCategory.id"
          :disabled="menuCategory.num_enabled_menu_items_used_on == 0"
          label
          class="body-1 ml-0 mr-2"
          :class="{
            'grey lighten-3 outlined--dark':
              !isActiveMenuCategory(menuCategory),
            'success white--text': isActiveMenuCategory(menuCategory),
            clickable: menuCategory.num_enabled_menu_items_used_on > 0,
          }"
          style="user-select: none"
          @click="
            menuCategory.num_enabled_menu_items_used_on > 0
              ? toggleActiveMenuCategory(menuCategory, $event)
              : null
          "
        >
          {{ menuCategory.name }}
        </v-chip>
      </v-layout>
      <!-- Choose MenuTemplate/TVFormat -->
      <v-layout
        justify-start
        :class="{
          'ml-4': !loading && !!menuTemplates && menuTemplates.length > 1,
        }"
      >
        <v-slide-x-transition>
          <v-btn-toggle
            v-if="!loading && !!menuTemplates && menuTemplates.length > 1"
            v-model="selectedMenuTemplateId"
            mandatory
            class="elevation-0"
          >
            <v-btn
              v-if="landscapeTemplate"
              flat
              round
              :value="landscapeTemplate.id"
            >
              <v-icon class="mr-2">
                crop_landscape
              </v-icon>
              {{ $t('landscapeShort') }}
            </v-btn>
            <v-btn
              v-if="portraitTemplate"
              flat
              round
              :value="portraitTemplate.id"
            >
              <v-icon class="mr-2">
                crop_portrait
              </v-icon>
              {{ $t('portraitShort') }}
            </v-btn>
          </v-btn-toggle>
        </v-slide-x-transition>
      </v-layout>
    </v-layout>
    <!-- Preview -->
    <tv-decoder-menu-preview
      v-show="!loading && currentMenu"
      :key="stringifiedCurrentMenu"
      :tvdecoder="tvdecoder"
      :preview-max-width="previewMaxWidth"
      :preview-max-height="previewMaxHeight"
      :busy="loadingPreview"
      class="unselectable mt-4"
    />
    <div
      v-if="!loading && !currentMenu"
      class="mt-4 title grey--text text-xs-center"
    >
      {{ $t('menuPreviewPleasePickAtLeastOneCategory') }}
    </div>
    <!-- Staff debugging only: Refresh button -->
    <v-btn
      v-if="$userprofile && $userprofile.is_being_impersonated"
      icon
      class="mr-0"
      :title="$t('reloadPage')"
      fixed
      bottom
      right
      @click="fetchMenuPreview()"
    >
      <v-icon class="grey--text text--darken-1">
        refresh
      </v-icon>
    </v-btn>
  </div>
</template>

<script>
import {mapState} from 'vuex';

import {deepCopyObject} from '@/utils';

import MenuPagesNavbar from '@/components/Menu/MenuPagesNavbar';
import TvDecoderMenuPreview from '@/components/DecoderDetail/TvDecoderMenuPreview';

export default {
  components: {
    MenuPagesNavbar,
    TvDecoderMenuPreview,
  },
  data() {
    return {
      loading: false,
      loadingPreview: false,

      selectedMenuTemplateId: null,
      activeMenuCategories: [],

      // From backend.
      menu: null,
      currentMenu: null,
    };
  },
  computed: {
    ...mapState({
      menuCategories: (state) => state.common.menuCategories,
      lastSelectedMenuCategory: (state) =>
        state.common.lastSelectedMenuCategory,
    }),
    stringifiedCurrentMenu() {
      return JSON.stringify(this.currentMenu);
    },
    menuCategoriesWithItems() {
      return this.menuCategories.filter(
        (category) => category.num_menu_items_used_on > 0
      );
    },
    menuCategoriesWithEnabledItems() {
      return this.menuCategories.filter(
        (category) => category.num_enabled_menu_items_used_on > 0
      );
    },
    menuId() {
      return this.$route.params.menuId;
    },
    /**
     * Return 'normal', non-highlight templates here only.
     */
    menuTemplates() {
      return (this.menu || {}).menu_templates.filter(
        (template) => !template.is_highlight_clip_template
      );
    },
    landscapeTemplate() {
      if (!this.menuTemplates) {
        return null;
      }
      return this.menuTemplates.filter(
        (t) => t.tvformat.aspect_ratio == 16 / 9
      )[0];
    },
    portraitTemplate() {
      if (!this.menuTemplates) {
        return null;
      }
      return this.menuTemplates.filter(
        (t) => t.tvformat.aspect_ratio == 9 / 16
      )[0];
    },
    selectedMenuTemplate() {
      if (!this.selectedMenuTemplateId) {
        return;
      }
      return this.menuTemplates.filter(
        (t) => t.id == this.selectedMenuTemplateId
      )[0];
    },
    // Virtual 16:9 TVDecoder object.
    tvdecoder() {
      const fallback = {
        tvformat: {
          width: 1920,
          height: 1080,
          aspect_ratio: 16 / 9,
        },
        current_menu: this.currentMenu,
      };
      if (!this.selectedMenuTemplate) {
        return fallback;
      }

      return {
        tvformat: this.selectedMenuTemplate.tvformat,
        current_menu: this.currentMenu,
      };
    },
    previewMaxWidth() {
      const modifier = this.$vuetify.breakpoint.lgAndUp ? 1.0 : 0.9;
      if (
        this.selectedMenuTemplate &&
        this.selectedMenuTemplate.tvformat.aspect_ratio > 1
      ) {
        return Math.min(1090, this.$vuetify.breakpoint.width * modifier);
      }
      return Math.min(720, this.$vuetify.breakpoint.width * modifier);
    },
    previewMaxHeight() {
      const modifier = this.$vuetify.breakpoint.lgAndUp ? 1.0 : 0.66;
      if (
        this.selectedMenuTemplate &&
        this.selectedMenuTemplate.tvformat.aspect_ratio > 1
      ) {
        return Math.min(720, this.$vuetify.breakpoint.height * modifier);
      }
      return Math.min(1090, this.$vuetify.breakpoint.height * modifier);
    },
    activeMenuCategoryIds() {
      return this.activeMenuCategories.map((category) => category.id);
    },
  },
  watch: {
    selectedMenuTemplateId: function (newMenuTemplateId, oldMenuTemplateId) {
      if (oldMenuTemplateId == null) {
        // Ignore initial preselect.
        return;
      }
      this.fetchMenuPreview();
    },
  },
  created() {
    this.loading = true;
    this.fetchMenu().then(() => {
      this.preselectMenuTemplate();

      this.$store.dispatch('LIST_MENU_CATEGORIES').then(() => {
        const firstMenuCategory = this.menuCategoriesWithEnabledItems[0];
        this.activeMenuCategories = [
          this.lastSelectedMenuCategory || firstMenuCategory,
        ];
        this.fetchMenuPreview().finally(() => {
          this.loading = false;
        });
      });
    });
  },
  methods: {
    preselectMenuTemplate() {
      if (!this.menuTemplates) {
        return;
      }
      if (this.menuTemplates.length == 1) {
        this.selectedMenuTemplateId = this.menuTemplates[0].id;
      } else if (this.landscapeTemplate) {
        this.selectedMenuTemplateId = this.landscapeTemplate.id;
      }
    },
    toggleActiveMenuCategory(menuCategory, clickEvent = null) {
      const shiftPressed = !!clickEvent && clickEvent.shiftKey;
      if (shiftPressed) {
        if (
          this.activeMenuCategories.length == 1 &&
          this.activeMenuCategories[0].id == menuCategory.id
        ) {
          // Select all except this category.
          this.activeMenuCategories = this.menuCategories.filter((category) => {
            return (
              category.num_enabled_menu_items_used_on > 0 &&
              category.id !== menuCategory.id
            );
          });
        } else {
          // Select only this category.
          this.activeMenuCategories = [menuCategory];
        }
      } else {
        if (!this.isActiveMenuCategory(menuCategory)) {
          // Add this category to selection.
          this.activeMenuCategories.push(menuCategory);
        } else {
          // Remove this category from selection.
          const index = this.activeMenuCategoryIds.indexOf(menuCategory.id);
          this.activeMenuCategories.splice(index, 1);
        }
      }

      this.fetchMenuPreview();
    },
    isActiveMenuCategory(menuCategory) {
      for (const category of this.activeMenuCategories) {
        if (category.id == menuCategory.id) {
          return true;
        }
      }
      return false;
    },
    fetchMenu() {
      return this.$api.retrieveMenu(this.menuId).then((response) => {
        this.menu = response.data;
      });
    },
    /**
     * Fetch a payload to preview currently selected MenuCategories.
     *
     * Note: Each request will cancel the previous one, if still around.
     *   That's why we need to handle cancellations a bit here, to keep
     *   the busy state correct.
     */
    fetchMenuPreview() {
      this.loadingPreview = true;

      return this.$api
        .getMenuPreview(
          this.menuId,
          this.selectedMenuTemplateId,
          this.activeMenuCategoryIds
        )
        .then((response) => {
          this.currentMenu = response.data;

          if (window.debug) {
            console.log();
            console.log(
              JSON.stringify(deepCopyObject(this.currentMenu), null, 2)
            );
            console.log();
          }

          this.loadingPreview = false;
        })
        .catch((error) => {
          if (error && error.message && error.name == 'CanceledError') {
            console.log('[DEBUG] Outdated menu page preview request cancelled');
          }
        });
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/pages/MenuPreview.vue